import { TemplatePrompts } from '@kimuno/api-sdk/lib/types/common/Conversation';
import { GeneratorConfig } from '../../../../utils/useAISuggestions';

export interface BaseFormField {
  name: FieldName;
  label: string;
  required?: boolean;
  requiredMessage?: string;
  placeholder?: string;
  isHalfWidth?: { xs: number; sm: number; md: number };
  // Also excludes the field from the submitted prompt while hidden
  visibleWhen?: { field: FieldName; value: string };
}

export interface TextInputField extends BaseFormField {
  type: 'text';
  maxLength?: number;
  multiline?: boolean;
  minRows?: number;
  showCharacterCount?: boolean;
}

export interface AISuggestionsField extends BaseFormField {
  type: 'aiSuggestions';
  multiline?: boolean;
  minRows?: number;
  maxLength?: number;
  showCharacterCount?: boolean;
  generatorConfig: GeneratorConfig;
  aiParams: FieldName[];
}
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectField extends BaseFormField {
  type: 'select';
  options: SelectOption[];
  defaultValue?: string;
  fullWidth?: boolean;
}

export interface MultiSelectField extends BaseFormField {
  type: 'multiselect';
  options: SelectOption[];
  defaultValues?: string[];
  displayLogic?: {
    singleItemText?: string; // Display the item name if only one selected
    multipleItemsText?: string; // Display this text if multiple selected
  };
  minSelections?: number; // Minimum number of items that must be selected
}

export interface DependentSelectField extends BaseFormField {
  type: 'dependentSelect';
  dependsOn: FieldName;
  optionGroups: Record<string, SelectOption[]>;
  defaultOptionIndex?: number;
  fullWidth?: boolean;
  // Disables the option currently selected in this sibling field
  excludeSelectedOf?: FieldName;
  // Also reset to default when this field changes to the given value
  alsoResetOn?: { field: FieldName; value: string };
}

export type FormField = TextInputField | SelectField | MultiSelectField | AISuggestionsField | DependentSelectField;
export const HALF_WIDTH_GRID_SIZE = { xs: 6, sm: 6, md: 6 } as const;

export interface TemplateFormConfig {
  templateId: TemplatePrompts;
  title: string;
  description: string;
  fields: FormField[];
  hideAddLinks?: boolean;
  // Renders the optional "Hinweise zu den Uploads" field beneath the uploaded
  // files. Only CREATE_PODCAST consumes it server-side.
  showAttachmentsHint?: boolean;
  hideFileUpload?: boolean;
  allowImageGeneration?: boolean;
  footerNote?: string;
}

enum Language {
  ARABIC = 'Arabic',
  GERMAN = 'German',
  ENGLISH = 'English',
  FRENCH = 'French',
  POLISH = 'Polish',
  ROMANIAN = 'Romanian',
  RUSSIAN = 'Russian',
  SPANISH = 'Spanish',
  TURKISH = 'Turkish',
  UKRAINIAN = 'Ukrainian',
  MISCELLANEOUS = 'Miscellaneous',
}

export enum FieldName {
  SUBJECT = 'Subject',
  CLASS_YEAR = 'Class years',
  LANGUAGE = 'Language',
  TOPIC = 'Topic',
  TASK_TYPE = 'Task type',
  DIFFICULTY = 'Difficulty',
  ADDITIONAL_INSTRUCTIONS = 'Additional instructions',
  LEARNING_GOAL = 'Learning goal',
  DURATION = 'Duration',
  LANGUAGE_STYLE = 'Language style',
  ESSAY_TYPE = 'Essay type',
  LENGTH = 'Length',
  NUMBER_OF_TASKS = 'Number of tasks',
  NUMBER_OF_LESSONS = 'Number of lessons',
  IMAGE_DESCRIPTION = 'Image description',
  IMAGE_STYLE = 'Image style',
  IMAGE_ORIENTATION = 'Image orientation',
  TARGET_LANGUAGE = 'Target language',
  PODCAST_FORMAT = 'Podcast format',
  PODCAST_DURATION = 'Podcast duration',
  SPEAKER_1 = 'Speaker 1',
  SPEAKER_2 = 'Speaker 2',
  CONTENT_DESCRIPTION = 'Content description',
}

export enum GroupWorkType {
  PROJECT_WORK = 'Project work',
  DISCUSSION = 'Discussion',
  CASE_STUDY = 'Case study',
  SIMULATION = 'Simulation',
  PRESENTATION = 'Presentation',
  PEER_REVIEW = 'Peer review',
  BRAINSTORMING = 'Brainstorming',
  WEBQUEST = 'Webquest',
  LEARNING_GAMES = 'Learning games',
  CREATIVE_WRITING = 'Creative writing',
  DEBATE = 'Debate',
  EXPERT_PANEL = 'Expert panel',
}

export enum TaskType {
  FILL_IN_THE_BLANKS = 'Fill in the blanks',
  MULTIPLE_CHOICE = 'Multiple choice',
  ORDERING = 'Ordering',
  OPEN_QUESTIONS = 'Open questions',
  TRUE_OR_FALSE = 'True or false',
  TEXT_CREATION = 'Text creation',
  APPLICATION_TASK = 'Application task',
}

export enum Duration {
  MINUTES_45 = '45 Minutes',
  MINUTES_90 = '90 Minutes',
}

export enum Difficulty {
  EASY = 'Easy',
  MEDIUM = 'Medium',
  HARD = 'Hard',
}

export enum GroupWorkDifficulty {
  EASY = 'Easy',
  MEDIUM = 'Medium',
  DEMANDING = 'Demanding',
}

export enum LanguageStyle {
  SIMPLE = 'Simple',
  SCHOOL = 'Scholar',
  DEMANDING = 'Demanding',
}

export enum EssayType {
  Expository = 'Expository',
  Argumentative = 'Argumentative',
  Comparative = 'Comparative',
  Literative = 'Literative',
}

export enum Length {
  SHORT = 'Short',
  MEDIUM = 'Medium',
  LONG = 'Long',
}

export enum WorksheetDifficulty {
  Reproduction = 'Reproduction',
  Application = 'Application',
  Transfer = 'Transfer',
}

export enum PodcastTargetLanguage {
  GERMAN = 'German',
  ENGLISH = 'English',
  FRENCH = 'French',
  SPANISH = 'Spanish',
}

export enum PodcastFormat {
  DIALOG = 'Dialog',
  EXPLANATION = 'Explanation podcast (monologue)',
  STORYTELLING = 'Storytelling',
}

export enum PodcastDuration {
  SHORT = 'Short (~1 min)',
  MEDIUM = 'Medium (~3 min)',
  LONG = 'Long (~5 min)',
}

const podcastFormatOptions: SelectOption[] = [
  { value: PodcastFormat.DIALOG, label: 'Dialog' },
  { value: PodcastFormat.EXPLANATION, label: 'Explanation podcast (monologue)' },
  { value: PodcastFormat.STORYTELLING, label: 'Storytelling' },
];

// Every language must offer the voices in this same underlying order, so that
// Speaker 1 and Speaker 2 always default to the same voice identity (one female,
// one male) whatever localized name is displayed. Reordering this array changes
// the default voices in every language.
const PODCAST_VOICE_ORDER = ['Leda', 'Achird', 'Vindemiatrix', 'Sadaltager'] as const;

type GeminiVoice = (typeof PODCAST_VOICE_ORDER)[number];

// The name -> Gemini voice resolution itself lives in APIv3, not here: we send
// the bare localized name as `speakers`. This map is the frontend's record of
// which voice each name stands for, and is what makes the ordering meaningful.
const podcastVoiceNames: Record<GeminiVoice, Record<PodcastTargetLanguage, SelectOption>> = {
  Leda: {
    [PodcastTargetLanguage.GERMAN]: { value: 'Anna', label: 'Anna \u2013 Weiblich, Klar' },
    [PodcastTargetLanguage.ENGLISH]: { value: 'Sarah', label: 'Sarah \u2013 Female, Calm' },
    [PodcastTargetLanguage.FRENCH]: { value: 'Marie', label: 'Marie \u2013 F\u00e9minin, Claire' },
    [PodcastTargetLanguage.SPANISH]: { value: 'Sofia', label: 'Sofia \u2013 Femenino, Fresco' },
  },
  Achird: {
    [PodcastTargetLanguage.GERMAN]: { value: 'Max', label: 'Max \u2013 M\u00e4nnlich, Standard' },
    [PodcastTargetLanguage.ENGLISH]: { value: 'James', label: 'James \u2013 Male, Motivating' },
    [PodcastTargetLanguage.FRENCH]: { value: 'Luc', label: 'Luc \u2013 Masculin, Profond' },
    [PodcastTargetLanguage.SPANISH]: { value: 'Diego', label: 'Diego \u2013 Masculino, Motivador' },
  },
  Vindemiatrix: {
    [PodcastTargetLanguage.GERMAN]: { value: 'Sophie', label: 'Sophie \u2013 Weiblich, Warm' },
    [PodcastTargetLanguage.ENGLISH]: { value: 'Emma', label: 'Emma \u2013 Female, Clear' },
    [PodcastTargetLanguage.FRENCH]: { value: 'C\u00e9line', label: 'C\u00e9line \u2013 F\u00e9minin, Douce' },
    [PodcastTargetLanguage.SPANISH]: { value: 'Carmen', label: 'Carmen \u2013 Femenino, Suave' },
  },
  Sadaltager: {
    [PodcastTargetLanguage.GERMAN]: { value: 'Leon', label: 'Leon \u2013 M\u00e4nnlich, Tief' },
    [PodcastTargetLanguage.ENGLISH]: { value: 'Michael', label: 'Michael \u2013 Male, Profound' },
    [PodcastTargetLanguage.FRENCH]: { value: 'Pierre', label: 'Pierre \u2013 Masculin, Standard' },
    [PodcastTargetLanguage.SPANISH]: { value: 'Carlos', label: 'Carlos \u2013 Masculino, Profundo' },
  },
};

const speakerOptionsFor = (language: PodcastTargetLanguage): SelectOption[] =>
  PODCAST_VOICE_ORDER.map(voice => podcastVoiceNames[voice][language]);

const podcastSpeakerOptionGroups: Record<PodcastTargetLanguage, SelectOption[]> = {
  [PodcastTargetLanguage.GERMAN]: speakerOptionsFor(PodcastTargetLanguage.GERMAN),
  [PodcastTargetLanguage.ENGLISH]: speakerOptionsFor(PodcastTargetLanguage.ENGLISH),
  [PodcastTargetLanguage.FRENCH]: speakerOptionsFor(PodcastTargetLanguage.FRENCH),
  [PodcastTargetLanguage.SPANISH]: speakerOptionsFor(PodcastTargetLanguage.SPANISH),
};

export const createClassYearOptions = () => {
  return Array.from({ length: 13 }, (_, i) => ({ value: `${i + 1}`, label: `${i + 1}` }));
};

const createSelectOptions = (enumType: Record<string, string>): SelectOption[] => {
  return Object.values(enumType)
    .map((enumValue) => ({ value: enumValue, label: enumValue }));
};

const createNumberOfTasksOptions = () => {
  const values = [1, 5, 10, 15, 20];
  return values.map((num) => ({ value: `${num}`, label: `${num}` }));
};

const createNumberOfLessonsOptions = () => {
  const values = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  return values.map((num) => ({ value: `${num}`, label: `${num}` }));
};

export const formConfigs: Partial<Record<TemplatePrompts, TemplateFormConfig>> = {
  [TemplatePrompts.CREATE_WORKSHEET_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_WORKSHEET_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Create worksheet',
    description: 'Create a differentiated worksheet to your topic.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.LANGUAGE,
        label: 'Language',
        type: 'select',
        options: createSelectOptions(Language),
        defaultValue: 'German',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.TASK_TYPE,
        label: 'Task type',
        type: 'multiselect',
        options: createSelectOptions(TaskType),
        defaultValues: [
          TaskType.FILL_IN_THE_BLANKS,
          TaskType.MULTIPLE_CHOICE,
          TaskType.OPEN_QUESTIONS,
          TaskType.TRUE_OR_FALSE],
        displayLogic: {
          multipleItemsText: 'Task Mix'
        },
        minSelections: 1,
      },
      {
        name: FieldName.DIFFICULTY,
        label: 'Difficulty',
        type: 'multiselect',
        options: createSelectOptions(WorksheetDifficulty),
        defaultValues: [
          WorksheetDifficulty.Reproduction,
          WorksheetDifficulty.Application,
          WorksheetDifficulty.Transfer,
        ],
        displayLogic: {
          multipleItemsText: 'Mixed'
        },
        minSelections: 1,
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ]
  },
  [TemplatePrompts.CREATE_LESSON_PLAN_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_LESSON_PLAN_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Lesson planning',
    description: 'Plan structurized lesson',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
      },
      {
        name: FieldName.DURATION,
        label: 'Duration',
        type: 'select',
        options: createSelectOptions(Duration),
        defaultValue: '45 Minutes'
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.LEARNING_GOAL,
        label: 'Learning goal',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 2,
        placeholder: 'ie. Understand the causes of the French Revolution',
        generatorConfig: {
          promptType: 'learning_goal',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.TOPIC],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.TOPIC,
        ],
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ],
  },
  [TemplatePrompts.CREATE_GROUP_WORK_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_GROUP_WORK_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Group work',
    description: 'Create creative and collaborative group tasks for your classroom.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.LANGUAGE,
        label: 'Language',
        type: 'select',
        options: createSelectOptions(Language),
        defaultValue: 'German',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.TASK_TYPE,
        label: 'Task type',
        type: 'select',
        options: createSelectOptions(GroupWorkType),
        defaultValue:
          GroupWorkType.PROJECT_WORK
      },
      {
        name: FieldName.DIFFICULTY,
        label: 'Difficulty',
        type: 'select',
        options: createSelectOptions(GroupWorkDifficulty),
        defaultValue: GroupWorkDifficulty.MEDIUM,
      },
      {
        name: FieldName.LEARNING_GOAL,
        label: 'Learning goal',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 2,
        placeholder: 'ie. Understand the causes of the French Revolution',
        generatorConfig: {
          promptType: 'learning_goal',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.TOPIC],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.TOPIC,
        ],
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ],
  },
  [TemplatePrompts.CREATE_ESSAY_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_ESSAY_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Essay',
    description: 'Create example essays for analysis.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.LANGUAGE,
        label: 'Language',
        type: 'select',
        options: createSelectOptions(Language),
        defaultValue: 'German',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.LANGUAGE_STYLE,
        label: 'Language style',
        type: 'select',
        options: createSelectOptions(LanguageStyle),
        defaultValue: LanguageStyle.SCHOOL
      },
      {
        name: FieldName.LENGTH,
        label: 'Length',
        type: 'select',
        options: createSelectOptions(Length),
        defaultValue: Length.MEDIUM
      },
      {
        name: FieldName.ESSAY_TYPE,
        label: 'Essay type',
        type: 'select',
        options: createSelectOptions(EssayType),
        defaultValue: EssayType.Expository,
        fullWidth: true,
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ]
  },
  [TemplatePrompts.CREATE_TOPIC_EXPLANATION_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_TOPIC_EXPLANATION_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Topic explanation',
    description: 'Create a simple explanation for your topic.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.LANGUAGE,
        label: 'Language',
        type: 'select',
        options: createSelectOptions(Language),
        defaultValue: 'German',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.LANGUAGE_STYLE,
        label: 'Language style',
        type: 'select',
        options: createSelectOptions(LanguageStyle),
        defaultValue: LanguageStyle.SCHOOL
      },
      {
        name: FieldName.LENGTH,
        label: 'Length',
        type: 'select',
        options: createSelectOptions(Length),
        defaultValue: Length.MEDIUM
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ]
  },
  [TemplatePrompts.CREATE_LEARNING_OBJECTIVE_ASSERTION_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_LEARNING_OBJECTIVE_ASSERTION_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Learning objective assertion',
    description: 'Create a learning objective assertion for your topic.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.LANGUAGE,
        label: 'Language',
        type: 'select',
        options: createSelectOptions(Language),
        defaultValue: 'German',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.TASK_TYPE,
        label: 'Task type',
        type: 'multiselect',
        options: createSelectOptions(TaskType),
        defaultValues: [
          TaskType.FILL_IN_THE_BLANKS,
          TaskType.MULTIPLE_CHOICE,
          TaskType.OPEN_QUESTIONS,
          TaskType.TRUE_OR_FALSE],
        displayLogic: {
          multipleItemsText: 'Task Mix'
        },
        minSelections: 1,
      },
      {
        name: FieldName.NUMBER_OF_TASKS,
        label: 'Number of tasks',
        type: 'select',
        options: createNumberOfTasksOptions(),
        defaultValue: '10',
      },
      {
        name: FieldName.LEARNING_GOAL,
        label: 'Learning goal',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 2,
        placeholder: 'ie. Understand the causes of the French Revolution',
        generatorConfig: {
          promptType: 'learning_goal',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.TOPIC],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.TOPIC,
        ],
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ]
  },
  [TemplatePrompts.CREATE_LESSON_SERIES_FREE_DRAFT]: {
    templateId: TemplatePrompts.CREATE_LESSON_SERIES_FREE_DRAFT,
    allowImageGeneration: true,
    title: 'Lesson series planning',
    description: 'Create a lesson series planning for your topic.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. History',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
      },
      {
        name: FieldName.NUMBER_OF_LESSONS,
        label: 'Number of lessons',
        type: 'select',
        options: createNumberOfLessonsOptions(),
        defaultValue: '6'
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. French Revolution',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.LEARNING_GOAL],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.LEARNING_GOAL,
        ],
      },
      {
        name: FieldName.LEARNING_GOAL,
        label: 'Competencies/Learning goal',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        placeholder: 'ie. Critical thinking',
        generatorConfig: {
          promptType: 'learning_goal',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR, FieldName.TOPIC],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
          FieldName.TOPIC,
        ],
      },
      {
        name: FieldName.ADDITIONAL_INSTRUCTIONS,
        label: 'Additional instructions',
        type: 'text',
        multiline: true,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'ie. Suitable for 20 children with different learning levels'
      }
    ]
  },
  [TemplatePrompts.CREATE_IMAGE]: {
    templateId: TemplatePrompts.CREATE_IMAGE,
    title: 'Generate AI image',
    description: 'Create perfect classroom images in seconds',
    hideAddLinks: true,
    fields: [
      {
        name: FieldName.IMAGE_DESCRIPTION,
        label: 'Describe the desired image',
        type: 'text',
        required: true,
        requiredMessage: 'Please describe the desired image',
        multiline: true,
        minRows: 3,
        placeholder:
          'e.g. A detailed diagram of the human heart with labelled chambers and arteries, '
          + 'medically accurate, for biology lessons',
      },
      {
        name: FieldName.IMAGE_STYLE,
        label: 'Select style',
        type: 'select',
        options: [
          { value: 'Automatic (recommended)', label: 'Automatic (recommended)' },
          { value: 'Photorealistic', label: 'Photorealistic' },
          { value: 'Drawing', label: 'Drawing' },
          { value: 'Comic style', label: 'Comic style' },
          { value: 'Painted', label: 'Painted' },
          { value: 'Flat illustration', label: 'Flat illustration' },
          { value: 'Polaroid', label: 'Polaroid' },
          { value: 'Children\'s book style', label: 'Children\'s book style' },
          { value: 'Line art', label: 'Line art' },
        ],
        defaultValue: 'Automatic (recommended)',
      },
      {
        name: FieldName.IMAGE_ORIENTATION,
        label: 'Image orientation',
        type: 'select',
        options: [
          { value: 'Square', label: 'Square' },
          { value: 'Landscape', label: 'Landscape' },
          { value: 'Portrait', label: 'Portrait' },
        ],
        defaultValue: 'Square',
      },
    ]
  },
  [TemplatePrompts.CREATE_PODCAST]: {
    templateId: TemplatePrompts.CREATE_PODCAST,
    title: 'Create podcast',
    description: 'Create authentic audio materials for your lessons',
    hideAddLinks: true,
    showAttachmentsHint: true,
    footerNote: 'A transcript for reading along will be created automatically.',
    fields: [
      {
        name: FieldName.SUBJECT,
        label: 'Subject',
        type: 'text',
        required: true,
        placeholder: 'ie. French',
      },
      {
        name: FieldName.CLASS_YEAR,
        label: 'Class years',
        type: 'select',
        options: createClassYearOptions(),
        defaultValue: '9',
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TARGET_LANGUAGE,
        label: 'Target language',
        type: 'select',
        options: createSelectOptions(PodcastTargetLanguage),
        defaultValue: PodcastTargetLanguage.GERMAN,
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.TOPIC,
        label: 'Topic',
        type: 'aiSuggestions',
        multiline: true,
        minRows: 1,
        maxLength: 300,
        showCharacterCount: true,
        placeholder: 'ie. Shopping at the supermarket',
        required: true,
        generatorConfig: {
          promptType: 'topic',
          relevantParams: [FieldName.SUBJECT, FieldName.CLASS_YEAR],
        },
        aiParams: [
          FieldName.SUBJECT,
          FieldName.CLASS_YEAR,
        ],
      },
      {
        name: FieldName.PODCAST_FORMAT,
        label: 'Podcast format',
        type: 'select',
        options: podcastFormatOptions,
        defaultValue: PodcastFormat.DIALOG,
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.PODCAST_DURATION,
        label: 'Duration (approx.)',
        type: 'select',
        options: [
          { value: PodcastDuration.SHORT, label: 'Short (~1 min)' },
          { value: PodcastDuration.MEDIUM, label: 'Medium (~3 min)' },
          { value: PodcastDuration.LONG, label: 'Long (~5 min)' },
        ],
        defaultValue: PodcastDuration.SHORT,
        isHalfWidth: HALF_WIDTH_GRID_SIZE,
      },
      {
        name: FieldName.SPEAKER_1,
        label: 'Speaker 1',
        type: 'dependentSelect',
        dependsOn: FieldName.TARGET_LANGUAGE,
        optionGroups: podcastSpeakerOptionGroups,
        excludeSelectedOf: FieldName.SPEAKER_2,
        alsoResetOn: { field: FieldName.PODCAST_FORMAT, value: PodcastFormat.DIALOG },
      },
      {
        name: FieldName.SPEAKER_2,
        label: 'Speaker 2',
        type: 'dependentSelect',
        dependsOn: FieldName.TARGET_LANGUAGE,
        optionGroups: podcastSpeakerOptionGroups,
        defaultOptionIndex: 1,
        excludeSelectedOf: FieldName.SPEAKER_1,
        alsoResetOn: { field: FieldName.PODCAST_FORMAT, value: PodcastFormat.DIALOG },
        visibleWhen: { field: FieldName.PODCAST_FORMAT, value: PodcastFormat.DIALOG },
      },
      {
        name: FieldName.CONTENT_DESCRIPTION,
        label: 'Content description',
        type: 'text',
        multiline: true,
        minRows: 3,
        maxLength: 1500,
        showCharacterCount: true,
        placeholder: 'Describe what should be covered in the podcast...',
      },
    ],
  },
};

// Helper function to get form configuration by template ID
export const getFormConfig = (templateId: TemplatePrompts | null): TemplateFormConfig | null => {
  if (!templateId) return null;
  return formConfigs[templateId] || null;
};
